import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types';
import { formatDuration } from '@/lib/catalog';

interface HeroCarouselProps {
  movies: Movie[];
  onPlay: (movie: Movie) => void;
}

export default function HeroCarousel({ movies, onPlay }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const total = movies.length;

  const goTo = useCallback((index: number) => {
    if (total === 0) return;
    setActive((index + total) % total);
  }, [total]);

  useEffect(() => {
    if (paused || total <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % total);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  if (total === 0) {
    return (
      <section className="bg-background-50 px-4 pb-16 pt-32">
        <div className="shell rounded-lg border border-background-300/70 bg-background-100 p-10 text-center">
          <h1 className="font-heading text-3xl text-foreground-950">今晚，从一部好电影开始</h1>
          <p className="mt-3 text-sm text-foreground-500">焦点内容正在更新中，先去看看片库里的高分作品吧。</p>
          <Link
            to="/movies"
            className="mt-6 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
          >
            浏览电影片库
          </Link>
        </div>
      </section>
    );
  }

  const current = movies[active];

  return (
    <section
      className="relative w-full overflow-hidden"
      aria-label="焦点影片推荐"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[700px] w-full md:h-[720px] lg:h-[760px]">
        <h1 className="sr-only">午夜电影网 - 高清电影在线播放与最新电影资讯</h1>
        {movies.map((movie, index) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === active ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
            aria-hidden={index !== active}
          >
            <img
              src={movie.backdrop}
              alt={`《${movie.title}》焦点剧照`}
              title={`${movie.title} - 午夜电影网焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-background-50/60" />

        <div className="shell relative flex h-full flex-col justify-end pb-14 pt-28 md:pb-28 md:pt-32">
          <div className="max-w-2xl animate-fade-up" key={current.id}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-[11px] font-medium text-background-50">
                <i className="ri-fire-fill text-[10px]" aria-hidden="true"></i>
                本周焦点
              </span>
              <span className="rounded-full border border-background-300/80 bg-background-50/40 px-3 py-1 text-[11px] text-foreground-700 backdrop-blur">
                {current.year} 最新上线
              </span>
              <span className="hidden rounded-full border border-background-300/80 bg-background-50/40 px-3 py-1 text-[11px] text-foreground-700 backdrop-blur sm:inline-flex">
                热度 {current.popularity}
              </span>
            </div>

            <h2 className="mt-5 font-heading text-5xl font-semibold leading-none tracking-wide text-foreground-950 sm:text-6xl md:text-7xl">
              {current.title}
            </h2>
            <p className="mt-3 text-[11px] tracking-[0.32em] text-primary-500 md:text-xs">{current.enTitle}</p>
            <p className="mt-4 max-w-xl text-sm leading-7 text-foreground-700 md:text-base">{current.tagline}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 md:text-sm">
              <span className="flex items-center gap-1 font-semibold text-primary-500">
                <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                {current.rating.toFixed(1)}
              </span>
              <span className="h-3 w-px bg-background-400/60" />
              <span>{current.region}</span>
              <span className="h-3 w-px bg-background-400/60" />
              <span>{formatDuration(current.duration)}</span>
              <span className="h-3 w-px bg-background-400/60" />
              <span>{current.genres.join(' / ')}</span>
            </div>

            <p className="mt-4 line-clamp-2 max-w-xl text-xs leading-6 text-foreground-500 md:line-clamp-3 md:text-sm md:leading-7">
              {current.synopsis}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => onPlay(current)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </button>
              <Link
                to={`/movie/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 bg-background-50/30 px-6 py-3 text-sm text-foreground-800 backdrop-blur transition-colors hover:border-primary-500/70 hover:text-primary-500"
              >
                查看详情
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>

          <div className="mt-8 flex items-end justify-between gap-6 md:mt-10">
            <div className="hide-scrollbar flex flex-1 gap-2 overflow-x-auto pb-1 md:gap-3">
              {movies.map((movie, index) => (
                <button
                  key={movie.id}
                  type="button"
                  onClick={() => goTo(index)}
                  aria-label={`切换到《${movie.title}》`}
                  className={`group flex w-28 shrink-0 flex-col gap-2 text-left md:w-40 ${
                    index === active ? 'opacity-100' : 'opacity-55 hover:opacity-90'
                  }`}
                >
                  <span
                    className={`h-0.5 w-full rounded-full transition-colors ${index === active ? 'bg-primary-500' : 'bg-foreground-500/30'}`}
                  />
                  <span className="truncate text-[11px] text-foreground-700 md:text-xs">
                    {String(index + 1).padStart(2, '0')} / {movie.title}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => goTo(active - 1)}
                aria-label="上一部焦点影片"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300/80 text-foreground-700 transition-colors hover:border-primary-500/70 hover:text-primary-500"
              >
                <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={() => goTo(active + 1)}
                aria-label="下一部焦点影片"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}